import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Children } from "@app/core/type/Children"
import { Organization } from "@app/core/dto/organization/Organization"
import { OrganizationRoleEnum } from "@app/core/enum/user/organization-role-enum"
import { useOrganization } from "@app/core/hooks/use-organization"
import { UserContext } from "@app/core/context/user/UserContext"

export const OrganizationContext = React.createContext<OrganizationStore>({} as OrganizationStore)

export function OrganizationContextProvider({ children }: Children): React.JSX.Element {
  const { user } = useContext(UserContext)
  const [organization, setOrganization] = useState<Organization | undefined>(undefined)
  const { fetchMyOrganization } = useOrganization()

  const refreshOrganization: () => Promise<Organization | undefined> = useCallback(() => {
    if (user?.role && user.role !== OrganizationRoleEnum.ULTIMATE_USER) {
      return fetchMyOrganization().then((newOrganization) => {
        setOrganization(newOrganization)
        return newOrganization
      })
    } else {
      setOrganization(undefined)
    }

    return Promise.resolve(undefined)
  }, [fetchMyOrganization, user])

  useEffect(() => {
    refreshOrganization()
  }, [refreshOrganization, user])

  const organizationStore: OrganizationStore = useMemo(
    () => ({ organization, setOrganization, refreshOrganization }),
    [organization, refreshOrganization]
  )
  return <OrganizationContext.Provider value={organizationStore}>{children}</OrganizationContext.Provider>
}

export interface OrganizationStore {
  organization: Organization | undefined
  setOrganization(organization: Organization): void
  refreshOrganization(): Promise<Organization | undefined>
}
